import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Chip,
  useTheme,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  FiberManualRecord as StatusIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  AdminPanelSettings as AdminIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import axios from 'axios';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import SuccessDialog from '../../components/SuccessDialog';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';

interface CompanyUser {
  _id: string;
  clientName: string;
  email: string;
  username: string;
  role: 'admin' | 'editor' | 'viewer';
  isActive: boolean;
  isOnline: boolean;
  lastLogin?: string;
  lastHeartbeat?: string;
  createdAt: string;
  companyId: string;
}

interface CompanyInfo {
  _id: string;
  companyName: string;
  companyEmail: string;
  companyIndustry: string;
  maxUsers: number;
  isActive: boolean;
  users: CompanyUser[];
  activeUsersCount: number;
}

const UserStatus: React.FC = () => {
  const theme = useTheme();
  const { user, isAuthenticated } = useAuth();
  const { isDarkMode } = useAppTheme();
  const [companyData, setCompanyData] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  
  
  // Success Dialog state
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState({
    title: '',
    message: '',
    autoCloseDelay: 3000,
  });

  const fetchCompanyUsers = useCallback(async () => {
    try {
      const response = await api.get('/api/client/company-users');
      
      if (response.data.success) {
        // The data is nested under response.data.data
        const apiData = response.data.data || response.data;
        setCompanyData({
          _id: apiData.companyId || '',
          companyName: apiData.companyName,
          companyEmail: '',
          companyIndustry: '',
          maxUsers: 0,
          isActive: true,
          users: apiData.users || [],
          activeUsersCount: apiData.totalUsers || 0
        });
      } else {
        setCompanyData(null);
      }
    } catch (error: any) {
      console.error('Error fetching company users:', error);
      setSuccessDialogConfig({
        title: 'Error Loading Users',
        message: error.response?.data?.message || 'Failed to fetch company users',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
      setCompanyData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch data if we're authenticated and have a user
    if (isAuthenticated && user) {
      fetchCompanyUsers();
    }
  }, [fetchCompanyUsers, isAuthenticated, user]);

  // Auto-refresh every 15 seconds to match heartbeat interval
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    
    const intervalId = setInterval(() => {
      fetchCompanyUsers();
    }, 15000);

    return () => clearInterval(intervalId);
  }, [fetchCompanyUsers, isAuthenticated, user]);

  const getUserOnlineStatus = (user: CompanyUser) => {
    return {
      isOnline: user.isOnline,
      statusText: user.isOnline ? 'Online' : 'Offline'
    };
  };



  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return <AdminIcon sx={{ fontSize: 16 }} />;
      case 'editor': return <SettingsIcon sx={{ fontSize: 16 }} />;
      case 'viewer': return <PeopleIcon sx={{ fontSize: 16 }} />;
      default: return <PeopleIcon sx={{ fontSize: 16 }} />;
    }
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'admin': return 'ADMIN';
      case 'editor': return 'SALES';
      case 'viewer': return 'VIEWER';
      default: return role.toUpperCase();
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'editor': return 'warning';
      case 'viewer': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!companyData) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Failed to load company data. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        p: 3,
        backgroundColor: theme.palette.custom.background.default,
        color: isDarkMode ? '#ffffff' : '#202224',
        minHeight: 'calc(100vh - 64px)',
        transition: 'background 0.5s ease-in-out',
        fontFamily: '"Nunito Sans", sans-serif',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography 
          variant="h2" 
          className={`user-status-title ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
          sx={{
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            fontSize: '2rem',
            color: isDarkMode ? '#FFFFFF' : '#202224',
            fontWeight: 700,
            fontFamily: '"Nunito Sans", sans-serif',
            backgroundColor: isDarkMode ? 'transparent' : '#F5F6FA',
          }}
        >
          User Status
        </Typography>
      </Box>

      {/* Company Overview Card */}
      {companyData && companyData.users ? (
        <Card sx={{ mb: 3, maxWidth: 300 }} className={!isDarkMode ? 'user-status-company-card-light' : ''}>
          <CardContent sx={{ py: 2 }} className={!isDarkMode ? 'user-status-company-content-light' : ''}>
            {/* Header Section - Icon left, Company info right */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }} className={!isDarkMode ? 'user-status-company-header-light' : ''}>
              <BusinessIcon color="primary" sx={{ fontSize: 28 }} className={!isDarkMode ? 'user-status-company-icon-light' : ''} />
              <Box sx={{ flex: 1 }} className={!isDarkMode ? 'user-status-company-info-light' : ''}>
                <Typography component="div" variant="h6" fontWeight="bold">
                  {companyData.companyName}
                </Typography>
                <Typography component="div" variant="body2" color="text.secondary">
                  {companyData.companyIndustry}
                </Typography>
              </Box>
            </Box>
            
            <Box width="100%" borderTop={1} borderColor="divider" mb={1} />
            
            {/* Statistics Section - Total Users left, Online Now right */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} className={!isDarkMode ? 'user-status-company-stats-light' : ''}>
              <Box textAlign="left" className={!isDarkMode ? 'user-status-stats-left-light' : ''}>
                <Typography component="div" variant="h6" fontWeight="bold" color="primary">
                  {companyData.users.length}
                </Typography>
                <Typography component="div" variant="caption" color="text.secondary">
                  Total Users
                </Typography>
              </Box>
              <Box textAlign="right" className={!isDarkMode ? 'user-status-stats-right-light' : ''}>
                <Typography component="div" variant="h6" fontWeight="bold" color="success.main">
                  {companyData.users.filter(u => u.isOnline).length}
                </Typography>
                <Typography component="div" variant="caption" color="text.secondary">
                  Online Now
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ mb: 3, maxWidth: 300 }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <BusinessIcon color="primary" sx={{ fontSize: 28 }} />
              <Box sx={{ flex: 1 }}>
                <Typography component="div" variant="h6" fontWeight="bold">
                  Loading...
                </Typography>
                <Typography component="div" variant="body2" color="text.secondary">
                  Fetching company data
                </Typography>
              </Box>
            </Box>
            
            <Box width="100%" borderTop={1} borderColor="divider" mb={1} />
            
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2}>
        {companyData?.users?.map((companyUser) => {
          const { isOnline, statusText } = getUserOnlineStatus(companyUser);
          return (
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4} key={companyUser._id}>
              <Card
                sx={{
                  borderRadius: '16px',
                  border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e0e0e0',
                  pt: '20px',
                  pb: '20px',
                  px: '20px',
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
                  height: '300px',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease-in-out',
                  boxShadow: isDarkMode 
                    ? '0 4px 12px rgba(0, 0, 0, 0.3)' 
                    : '0 4px 12px rgba(0, 0, 0, 0.08)',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: isDarkMode 
                      ? '0 12px 32px rgba(255, 255, 255, 0.15)' 
                      : '0 12px 32px rgba(0, 0, 0, 0.2)',
                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : '#1B4672',
                  },
                }}
              >
                <CardContent sx={{ 
                  p: 0, 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}>
                  
                  {/* Header Section - Name + Status */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', px: 0 }}>
                    <Typography component="div" variant="h4" sx={{ 
                      fontWeight: 800, 
                      color: isDarkMode ? '#ffffff' : '#1B4672',
                      wordBreak: 'break-word',
                      lineHeight: 1.2,
                      flex: 1,
                      mr: 1,
                      minHeight: '2.4em', // Ensures 2 lines height
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <span dangerouslySetInnerHTML={{
                        __html: companyUser.clientName.split(' ').length === 2 
                          ? companyUser.clientName.replace(' ', '<br>') 
                          : companyUser.clientName + '<br>&nbsp;' // Add empty line for single names
                      }} />
                    </Typography>
                    
                    <Typography
                      component="div"
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        color: isOnline ? theme.palette.success.main : theme.palette.error.main,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        marginLeft: 'auto',
                        marginRight: -1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: isOnline ? theme.palette.success.main : theme.palette.error.main,
                        }}
                      />
                      <span>{isOnline ? 'Online' : 'Offline'}</span>
                    </Typography>
                  </Box>

                  {/* User Details Section */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    {/* Username */}
                    <Box sx={{ mb: 1.5 }}>
                      <Typography component="div" variant="body1" sx={{ 
                        color: theme.palette.text.primary, 
                        fontWeight: 400,
                        wordBreak: 'break-word',
                        lineHeight: 1.4
                      }}>
                        Username: <span className="user-status-username">{companyUser.username}</span>
                      </Typography>
                    </Box>

                    {/* Email */}
                    <Box>
                      <Typography component="div" variant="body1" sx={{ 
                        color: theme.palette.text.primary, 
                        fontWeight: 400,
                        wordBreak: 'break-word',
                        lineHeight: 1.4,
                        overflowWrap: 'break-word'
                      }}>
                        Email: {companyUser.email}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Role Section */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography component="div" variant="body2" sx={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(32, 34, 36, 0.7)' }}>Role:</Typography>
                      <Chip
                        icon={getRoleIcon(companyUser.role)}
                        label={getRoleDisplayName(companyUser.role)}
                        size="small"
                        sx={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: isDarkMode ? '#ffffff' : '#1B4672',
                          fontWeight: 600,
                          '& .MuiChip-icon': {
                            color: isDarkMode ? '#ffffff' : '#1B4672',
                          }
                        }}
                      />
                    </Box>
                  </Box>
                  
                </CardContent>
              </Card>
            </Grid>
          );
        }) || []}
        
        {/* Loading or Empty State */}
        {loading && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }} className={!isDarkMode ? 'user-status-loading-center-light' : ''}>
              <CircularProgress />
            </Box>
          </Grid>
        )}
        
        {!loading && (!companyData?.users || companyData.users.length === 0) && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }} className={!isDarkMode ? 'user-status-no-users-light' : ''}>
              <Typography variant="h6" color="text.secondary">
                No users found
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Success Dialog */}
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={successDialogConfig.title}
        message={successDialogConfig.message}
        buttonText="Got it"
        autoCloseDelay={successDialogConfig.autoCloseDelay}
      />
    </Box>
  );
};

export default UserStatus;